import { Child } from 'src/modules/Child/models/Child';
import { useRepo } from 'pinia-orm';
import { computed, ref } from 'vue';
import {
  useStrapiDelete,
  useStrapiGet,
  useStrapiPost,
  useStrapiPut,
} from 'src/shared/composables/useStrapiApi';
import { Dialog } from 'quasar';
import { t } from 'boot/i18n';

const childRepo = useRepo(Child);
const currentChild = ref<Child>();
const isEditing = ref(false);
const dialogContent = ref<'addEditChild' | 'loginToken'>('addEditChild');
const showDialog = ref(false);
const childLoginToken = ref<string>();

const editChild = (child: Child) => {
  isEditing.value = true;
  currentChild.value = child;
  dialogContent.value = 'addEditChild';
  showDialog.value = true;
  console.log('editChild', child);
};

const updateChild = async (
  childData = currentChild.value as Partial<Child>,
) => {
  showDialog.value = false;

  if (!childData || !childData?.documentId) return;

  // The backend resolves the public-school relation from its documentId.
  const publicSchoolDocumentId = childData.public_school?.documentId;

  const { execute, error, isFinished } = useStrapiPut(
    `users-permissions/users/child/${childData.documentId}`,
    {
      ...childData,
      ...(publicSchoolDocumentId ? { publicSchoolDocumentId } : {}),
    },
  );

  await execute();

  if (error.value) {
    console.error('Error updating child record', error.value);
  } else if (isFinished.value) {
    // Update only the editable scalar fields (name is common identity on the child's
    // user; keep id/documentId/user_id out of the payload so the ORM record — and the
    // card — refresh reliably).
    childRepo.where('documentId', childData.documentId).update({
      ...(childData.firstname !== undefined
        ? { firstname: childData.firstname }
        : {}),
      ...(childData.lastname !== undefined
        ? { lastname: childData.lastname }
        : {}),
      ...(childData.school !== undefined ? { school: childData.school } : {}),
      ...(childData.class !== undefined ? { class: childData.class } : {}),
      ...(childData.is_active !== undefined
        ? { is_active: childData.is_active }
        : {}),
      ...(childData.public_school !== undefined
        ? { public_school: childData.public_school ?? null }
        : {}),
    });
    console.log('Child record updated successfully');
    currentChild.value = undefined;
  }
};

const deleteChild = (child: Child) => {
  console.log('deleteChild', child);
  Dialog.create({
    title: t('CONFIRM'),
    message: t('ARE_YOU_SURE_YOU_WANT_TO_DELETE'),
    cancel: true,
    dark: true,
    ok: {
      color: 'negative',
      label: t('DELETE'),
    },
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
  }).onOk(async () => {
    console.log('deleteChild:', `children/${child.documentId}`);

    const { execute, error, isFinished } = useStrapiDelete(
      `children/${child.documentId}`,
    );
    await execute();
    if (error.value) {
      console.error('Error deleting child record', error.value);
    } else if (isFinished.value) {
      childRepo.where('id', child.id).delete();
    }
  });
};

const loginToken = async (child: Child) => {
  const { execute, data, error } = useStrapiGet(
    `users-permissions/users/child/token/${child.documentId}`,
  );

  await execute();

  if (error.value) {
    console.error('Error fetching login token', error.value);
  } else if (data.value) {
    childLoginToken.value = data.value?.token;
    dialogContent.value = 'loginToken';
    currentChild.value = child;
    showDialog.value = true;
  }
};

const toggleChildActive = (child: Child) => {
  Dialog.create({
    title: t('CONFIRM'),
    message: t('ARE_YOU_SURE_YOU_WANT_TO_TOGGLE_ACTIVE'),
    cancel: true,
    dark: true,
    ok: {
      color: 'warning',
      label: t('TOGGLE'),
    },
  }).onOk(() => {
    const { documentId, is_active: currentIsActive } = child;
    void updateChild({ documentId, is_active: !currentIsActive });
  });
};

const newChild = () => {
  isEditing.value = false;
  currentChild.value = {
    firstname: '',
    lastname: '',
    school: '',
    class: 0,
  } as Child;
  showDialog.value = true;
};

const getCheckoutData = async (period: 'month' | 'year', childId: number) => {
  const {
    data: planData,
    execute,
    isFinished,
    error,
  } = useStrapiGet('liqpay-plugin/pay/checkout', {
    params: {
      period,
      childId,
    },
  });

  await execute();

  if (error.value) {
    console.error('Error buying license', error.value);
  } else if (isFinished.value) {
    console.log('License bought successfully');
  }
  return planData.value;
};

const getChildLicenseData = async (childId: number) => {
  const params = {
    fields: ['id'],
    // children live under the parent collection now (backend Phase 4).
    populate: {
      parent: {
        populate: {
          children: {
            populate: {
              license: {
                fields: ['assigned_at', 'valid_until'],
                sort: { valid_until: 'desc' },
              },
            },
            filters: {
              id: {
                $eq: childId,
              },
            },
          },
        },
      },
    },
  };
  const url = 'users/me';
  const { execute, error, data: childLicenseData } = useStrapiGet(url, params);

  await execute();

  if (error.value) {
    console.error('Error fetching child license data', error.value);
    return;
  }

  const childFromParent = childLicenseData.value?.parent?.children?.[0];
  let childRes;
  if (childFromParent) {
    // Update the child repo with the received data
    childRes = childRepo
      .where('id', childId)
      .update(childFromParent);
    console.log('Child license data fetched successfully');
  }
  return childRes;
};

export const useManageChild = (currentUserId: number) => {
  const children = computed(() =>
    childRepo.where('user_id', currentUserId).all(),
  );

  const saveNewChild = async () => {
    showDialog.value = false;

    // Create new child in Strapi under current user
    if (!currentUserId) return;

    // The backend resolves the public-school relation from its documentId.
    const publicSchoolDocumentId = currentChild.value?.public_school?.documentId;

    const { execute, error, data, isFinished } = useStrapiPost(
      `users-permissions/users/child/new`,
      {
        ...currentChild.value,
        ...(publicSchoolDocumentId ? { publicSchoolDocumentId } : {}),
      },
    );

    await execute();

    if (error.value) {
      console.error('Error creating child record', error.value);
    } else if (isFinished.value && data.value) {
      if (currentChild.value) {
        // The create response omits the populated relation; merge the locally
        // selected public-school so the card shows school + city immediately.
        childRepo.save({
          ...data.value?.newChild,
          public_school: currentChild.value.public_school ?? null,
        });
      }
      console.log('Child record created successfully', data.value.newChild);
    }
  };

  return {
    currentChild,
    isEditing,
    dialogContent,
    showDialog,
    children,
    childLoginToken,
    getChildLicenseData,
    getCheckoutData,
    loginToken,
    newChild,
    toggleChildActive,
    editChild,
    saveNewChild,
    updateChild,
    deleteChild,
  };
};
